/**
 * `PlatformPayments.blockchypLocalTransport()` on Electron — BlockChyp
 * LOCAL_NETWORK (LAN) terminals from the Windows register (Workstream A).
 *
 * ## Why this exists
 *
 * The owner's terminal runs in BlockChyp **local mode**. Phase 1 made desktop
 * BlockChyp CLOUD-only, so every LOCAL_NETWORK sale was refused before a charge
 * was attempted ("payment option refused"). The shared V5 LAN flow —
 * `adapters/blockchyp/native-charge.ts` and `terminal-status/blockchyp-lan.ts` —
 * already talks to a plugin-shaped factory (`newLocalNetworkClient` /
 * `newClient`) and already resolves it through this seam. This file is the desktop answer: the same factory shape,
 * executing in the Electron **main process** over the allow-listed
 * `pc:payments:*` channels. No flow logic lives here; the engines are unchanged.
 *
 * ## What the renderer can and cannot do through it
 *
 * - It names a terminal **by name only**. Main resolves the LAN address from
 *   the BlockChyp gateway and refuses anything but a private IPv4. The
 *   plugin's `ipAddressHint` option is accepted for signature compatibility
 *   and **deliberately dropped**; `newRoutedClient` / `newCloudRelayClient`
 *   (renderer-seeded routes) are refused outright.
 * - Credentials pass through once, lazily, into main's custody
 *   (`pc:payments:provision-credentials`, idempotent in main), and this module
 *   drops its reference as soon as that call settles — the plugin facade's own
 *   rule. Nothing it returns carries a credential, a route or an address.
 *
 * ## Error vocabulary — the part the engines depend on
 *
 * The shared classifiers decide FAILED (safe) vs UNKNOWN (ambiguous) from the
 * error `code` / `data.details.stage`, exactly as they do for the native plugin:
 *
 * | Main refusal | Thrown `code` | Engine outcome |
 * |---|---|---|
 * | `PAYMENT_ROUTE_UNAVAILABLE` (nothing sent) | `BLOCKCHYP_LAN_ROUTE_UNAVAILABLE`, stage `terminal_route_lookup` | FAILED — safe to retry |
 * | `PAYMENT_TERMINAL_UNTRUSTED` (pinned TLS refused the terminal; nothing sent, T4-03) | `BLOCKCHYP_LAN_ROUTE_UNAVAILABLE`, stage `terminal_tls_verification` | FAILED — safe to retry |
 * | `PAYMENT_UNRESOLVED_DISPATCH` (earlier charge open) | `BLOCKCHYP_OPERATION_CONFLICT` | V5: FAILED "terminal busy"; V4: UNKNOWN → reconcile (iOS parity) |
 * | anything else, incl. `PAYMENT_DUPLICATE_DISPATCH` | `BLOCKCHYP_TRANSPORT_ERROR` | UNKNOWN — resolve by `transactionStatus` |
 *
 * Nothing here ever turns a post-dispatch failure into FAILED.
 */
import type {
  BridgeResult,
  ChannelRequest,
  ChannelResponse,
} from 'electron-shared/bridge-types';
import type {
  BlockChypCredentials,
  BlockChypBaseResponse,
  BlockChypLocalNetworkClientOptions,
  BlockChypRouteCacheDiagnosticsRequest,
  BlockChypRouteCacheDiagnosticsResponse,
  BlockChypClient as BlockChypCompatibleClient,
} from '@am3nd-org/pointcraft-blockchyp';

import { paymentsBridge } from './bridge';
import { PlatformCapabilityUnsupportedError } from '../errors';
import type { BlockChypLocalTransport } from '../services/types';
import { noteElectronChargeEnvelope } from './dispatch-reconcile.electron';

type WireTransport = 'lan-required' | 'provider-default';

export type DesktopBlockChypErrorCode =
  | 'BLOCKCHYP_LAN_ROUTE_UNAVAILABLE'
  | 'BLOCKCHYP_OPERATION_CONFLICT'
  | 'BLOCKCHYP_INVALID_REQUEST'
  | 'BLOCKCHYP_TRANSPORT_ERROR';

/** Plugin-shaped rejection: `code` + `data.details`, like the native bridge's. */
export class DesktopBlockChypError extends Error {
  readonly code: DesktopBlockChypErrorCode;

  readonly data: { details: Record<string, unknown> };

  readonly bridgeErrorCode: string | null;

  constructor(
    code: DesktopBlockChypErrorCode,
    message: string,
    details: Record<string, unknown>,
    bridgeErrorCode: string | null
  ) {
    super(message);
    this.name = 'DesktopBlockChypError';
    this.code = code;
    this.data = { details };
    this.bridgeErrorCode = bridgeErrorCode;
  }
}

const DEFAULT_TIMEOUT_SECONDS = 30;

function positiveTimeout(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.min(Math.trunc(value), 600)
    : undefined;
}

function invalid(field: string): DesktopBlockChypError {
  return new DesktopBlockChypError(
    'BLOCKCHYP_INVALID_REQUEST',
    `The desktop BlockChyp bridge requires a valid '${field}'.`,
    { stage: 'request_validation', transport: 'desktop-bridge' },
    null
  );
}

function requireString(value: unknown, field: string): string {
  if (typeof value !== 'string' || value.length === 0) throw invalid(field);
  return value;
}

/** Main's `validationStatus` in the plugin's diagnostic vocabulary. */
function routeDiagnosticDetails(details: Record<string, string> | undefined) {
  const status = details?.validationStatus;
  return {
    stage: 'terminal_route_lookup',
    transport: 'LAN',
    selectedTransport: 'LAN',
    ...(status
      ? {
          validationReason:
            status === 'incomplete_credentials' ? 'incomplete_transient_credentials' : status,
        }
      : {}),
    ...(status === 'invalid_local_address' ? { localAddressValid: false } : {}),
    ...(status === 'incomplete_credentials' ? { transientCredentialsPresent: false } : {}),
  };
}

/**
 * A bridge envelope → the value, or the plugin-shaped rejection the engines
 * already classify. `operation` only labels the diagnostic stage.
 */
function settle<T>(result: BridgeResult<T>, operation: string, transport: WireTransport): T {
  if (result.ok) return result.value;
  const { code, message, details } = result.error;

  if (code === 'PAYMENT_ROUTE_UNAVAILABLE') {
    throw new DesktopBlockChypError(
      'BLOCKCHYP_LAN_ROUTE_UNAVAILABLE',
      message,
      routeDiagnosticDetails(details),
      code
    );
  }
  if (code === 'PAYMENT_TERMINAL_UNTRUSTED') {
    // T4-03: main refused the terminal's certificate during the handshake,
    // before any request byte was written — provably nothing dispatched, so it
    // takes the engines' pre-dispatch (FAILED, safe) arm.
    throw new DesktopBlockChypError(
      'BLOCKCHYP_LAN_ROUTE_UNAVAILABLE',
      message,
      {
        stage: 'terminal_tls_verification',
        transport: 'LAN',
        selectedTransport: 'LAN',
        validationReason: 'terminal_untrusted',
      },
      code
    );
  }
  if (code === 'PAYMENT_UNRESOLVED_DISPATCH') {
    throw new DesktopBlockChypError(
      'BLOCKCHYP_OPERATION_CONFLICT',
      message,
      {
        stage: 'desktop_unresolved_dispatch',
        transport: transport === 'lan-required' ? 'LAN' : 'PROVIDER_DEFAULT',
      },
      code
    );
  }
  throw new DesktopBlockChypError(
    'BLOCKCHYP_TRANSPORT_ERROR',
    message,
    {
      stage: `desktop_${operation}`,
      transport: transport === 'lan-required' ? 'LAN' : 'PROVIDER_DEFAULT',
    },
    code
  );
}

interface ClientScope {
  readonly transport: WireTransport;
  /** A LAN client is locked to one terminal/environment, as the plugin's is. */
  readonly terminalName?: string;
  readonly test?: boolean;
}

function createClient(
  credentials: BlockChypCredentials,
  scope: ClientScope
): BlockChypCompatibleClient {
  let pendingCredentials: BlockChypCredentials | null = {
    apiKey: credentials.apiKey,
    bearerToken: credentials.bearerToken,
    signingKey: credentials.signingKey,
  };
  let provisioning: Promise<void> | null = null;

  /** Provisions once per client, then forgets the credentials either way. */
  const ensureProvisioned = (): Promise<void> => {
    if (!provisioning) {
      const toSend = pendingCredentials;
      provisioning = (async () => {
        try {
          if (!toSend) throw invalid('credentials');
          settle(await paymentsBridge().provisionCredentials(toSend), 'provision', scope.transport);
        } finally {
          pendingCredentials = null;
        }
      })();
    }
    return provisioning;
  };

  const lockToScope = (terminalName: string, test: boolean): void => {
    if (scope.terminalName !== undefined && terminalName !== scope.terminalName) {
      throw invalid('terminalName');
    }
    if (scope.test !== undefined && test !== scope.test) throw invalid('test');
  };

  const transportField =
    scope.transport === 'lan-required' ? { transport: 'lan-required' as const } : {};

  const client = {
    async charge(request: Record<string, unknown>) {
      const terminalName = requireString(request.terminalName, 'terminalName');
      const test = request.test === true;
      lockToScope(terminalName, test);
      const timeout = positiveTimeout(request.timeout);
      if (timeout === undefined) throw invalid('timeout');
      await ensureProvisioned();
      const envelope = await paymentsBridge().charge({
        terminalName,
        amount: requireString(request.amount, 'amount'),
        transactionRef: requireString(request.transactionRef, 'transactionRef'),
        test,
        promptForTip: request.promptForTip === true,
        description: typeof request.description === 'string' ? request.description : '',
        timeout,
        ...(typeof request.force === 'boolean' ? { force: request.force } : {}),
        ...(typeof request.async === 'boolean' ? { async: request.async } : {}),
        ...transportField,
      });
      // T4-07: observation only — the reconcile panel learns of a stuck
      // attempt; what this charge throws or returns is unchanged.
      noteElectronChargeEnvelope(envelope, { terminalName, test });
      const value = settle(envelope, 'charge', scope.transport);
      return { data: value };
    },

    async clear(request: Record<string, unknown>) {
      const terminalName = requireString(request.terminalName, 'terminalName');
      const test = request.test === true;
      lockToScope(terminalName, test);
      await ensureProvisioned();
      const timeout = positiveTimeout(request.timeout);
      const value = settle(
        await paymentsBridge().clear({
          terminalName,
          test,
          ...(timeout === undefined ? {} : { timeout }),
          ...transportField,
        }),
        'clear',
        scope.transport
      );
      return { data: value };
    },

    async terminalStatus(request: Record<string, unknown>) {
      const terminalName = requireString(request.terminalName, 'terminalName');
      const test = request.test === true;
      lockToScope(terminalName, test);
      await ensureProvisioned();
      const value = settle(
        await paymentsBridge().terminalStatus({
          terminalName,
          test,
          timeout: positiveTimeout(request.timeout) ?? DEFAULT_TIMEOUT_SECONDS,
          ...transportField,
        }),
        'terminal_status',
        scope.transport
      );
      return { data: value };
    },

    async transactionStatus(request: Record<string, unknown>) {
      const transactionRef =
        typeof request.transactionRef === 'string' && request.transactionRef
          ? request.transactionRef
          : undefined;
      const transactionId =
        typeof request.transactionId === 'string' && request.transactionId
          ? request.transactionId
          : undefined;
      if (!transactionRef && !transactionId) throw invalid('transactionRef');
      await ensureProvisioned();
      // Always the gateway (`/api/tx-status`) — the financial authority.
      const value = settle(
        await paymentsBridge().transactionStatus({
          ...(transactionRef ? { transactionRef } : {}),
          ...(transactionId ? { transactionId } : {}),
          test: request.test === true,
          timeout: positiveTimeout(request.timeout) ?? DEFAULT_TIMEOUT_SECONDS,
        }),
        'transaction_status',
        scope.transport
      );
      return { data: value };
    },

    /**
     * The plugin's `release` hands its single native slot back. Main has no
     * such slot — the official SDK is stateless per request, and the dispatch
     * journal (not a client object) is what guards a charge — so there is
     * nothing to release. It must not call `pc:payments:release`, which would
     * drop main's credentials under a concurrent status read.
     */
    async release(): Promise<void> {
      pendingCredentials = null;
    },
  };

  return client as unknown as BlockChypCompatibleClient;
}

const refuseSeededRoute = (factory: string) => (): never => {
  throw new PlatformCapabilityUnsupportedError(
    `blockchyp.${factory}`,
    'electron',
    `'${factory}' seeds a terminal route from the renderer. The desktop register only uses a ` +
      'route main resolves from the BlockChyp gateway itself, so it is not available here.'
  );
};

/** Main's in-memory answer, in the plugin's diagnostics vocabulary. */
async function routeCacheDiagnostics(
  request: BlockChypRouteCacheDiagnosticsRequest
): Promise<BlockChypRouteCacheDiagnosticsResponse> {
  // Provision first (idempotent in main) so the diagnostics read the same
  // credential scope main will charge with.
  settle(
    await paymentsBridge().provisionCredentials({
      apiKey: request.credentials.apiKey,
      bearerToken: request.credentials.bearerToken,
      signingKey: request.credentials.signingKey,
    }),
    'provision',
    'lan-required'
  );
  const value = settle(
    await paymentsBridge().lanRouteDiagnostics({
      terminalName: request.terminalName,
      test: request.test,
    }),
    'route_diagnostics',
    'lan-required'
  );

  return {
    success: true,
    // Desktop keeps no protected on-disk route (no offline store-and-forward
    // route cache): `usable` means "main holds a validated LAN route for this
    // terminal in memory for this session", never "survives a restart".
    supported: true,
    routePresent: value.routePresent,
    source: 'none',
    readStatus: 'unsupported',
    validationStatus:
      value.validationStatus === 'not_found' ? 'not_applicable' : value.validationStatus,
    environment: request.test ? 'test' : 'production',
    usable: value.usable,
    ...(value.expiresAt ? { expiresAt: value.expiresAt } : {}),
    localAddressValid: value.localAddressValid,
    transientCredentialsComplete: value.transientCredentialsComplete,
    ...(value.providerCloudRelayEnabled === null
      ? {}
      : { providerCloudRelayEnabled: value.providerCloudRelayEnabled }),
    selectedTransport: 'LAN',
    cloudLookupRequired: !value.usable,
  };
}

/**
 * T4-05 / T4-07 — the operator's explicit way past a payment main cannot
 * confirm (`pc:payments:reconcile-dispatch`). Main asks the gateway first and
 * logs every use; it refuses while the terminal may still be running the
 * charge. Rejects with a `DesktopBlockChypError` on refusal. Only ever call
 * this from a deliberate manager action, never from an automatic flow.
 */
export async function reconcileElectronBlockChypDispatch(
  request: ChannelRequest<'pc:payments:reconcile-dispatch'>
): Promise<ChannelResponse<'pc:payments:reconcile-dispatch'>> {
  return settle(
    await paymentsBridge().reconcileDispatch(request),
    'reconcile_dispatch',
    'lan-required'
  );
}

/** The desktop `BlockChypLocalTransport`. Stateless; safe to share. */
export function createElectronBlockChypLocalTransport(): BlockChypLocalTransport {
  return {
    newLocalNetworkClient: (
      credentials: BlockChypCredentials,
      options: BlockChypLocalNetworkClientOptions
    ) =>
      // `options.ipAddressHint` is intentionally NOT forwarded: the address
      // is main's, from the gateway, validated private — never the renderer's.
      createClient(credentials, {
        transport: 'lan-required',
        terminalName: options.terminalName,
        test: options.test,
      }),
    newClient: (credentials: BlockChypCredentials) =>
      createClient(credentials, { transport: 'provider-default' }),
    newRoutedClient: refuseSeededRoute('newRoutedClient'),
    newCloudRelayClient: refuseSeededRoute('newCloudRelayClient'),
    routeCacheDiagnostics,
    async clearRouteCache(): Promise<BlockChypBaseResponse> {
      // Logout / revocation boundary: dropping main's client drops its route
      // cache with it. Credentials stay in the encrypted vault (A1.5).
      settle(await paymentsBridge().release({}), 'release', 'lan-required');
      return { success: true };
    },
  };
}
